import * as React from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import i18n from 'i18next';

/**
 * The portal's announcements (operator-panel-v2 T2.7, migration 0240; fleet-driver-pay T5.3, 0285/0286).
 *
 * - `useAnnouncements` feeds the banner: `my_announcements()`, the notices live now for the caller's
 *   agencies, minus the dismissible ones the caller dismissed (the server filters them). Re-read when the tab
 *   comes back (visibility, window focus), every `REFRESH_MS`, and when another surface changes the read
 *   state (`ANNOUNCEMENTS_CHANGED`). A failed read keeps the last rows and logs a warning: the banner is never
 *   an error wall.
 * - Dismissal is on the server (`announcement_dismiss`): the card hides at once, comes back with a toast
 *   when the server refuses. The old per-browser list (`DISMISSED_KEY`) is migrated once: each remembered
 *   id the server still returns is sent, then the key is removed (kept when a send fails, so the next visit
 *   retries). Nothing is written to localStorage any more.
 * - `useAnnouncementInbox` pages `my_announcement_inbox` (everything received, read or not).
 * - `useAnnouncementsUnread` is the topbar count (`my_announcements_unread_count`).
 */

export const REFRESH_MS = 5 * 60 * 1000;
/** The pre-0286 per-browser dismissal list; read once for the migration, then removed. */
export const DISMISSED_KEY = 'coreway.announcements.dismissed';
/** A window event: some surface changed the caller's read state; every reader re-reads. */
export const ANNOUNCEMENTS_CHANGED = 'coreway:announcements-changed';
/** Rows per inbox page (`my_announcement_inbox` accepts 1..200). */
export const INBOX_PAGE = 50;

export const ANNOUNCEMENT_KINDS = ['info', 'maintenance', 'terms', 'pricing', 'release'] as const;
export type AnnouncementKind = (typeof ANNOUNCEMENT_KINDS)[number];

export interface Announcement {
  id: string;
  kind: AnnouncementKind;
  title_en: string;
  title_es: string;
  body_en: string;
  body_es: string;
  starts_at: string;
  ends_at: string;
  dismissible: boolean;
  link_path: string | null;
  link_label_en: string | null;
  link_label_es: string | null;
}

export interface InboxAnnouncement extends Announcement {
  state: 'active' | 'ended';
  /** When the caller dismissed or marked it read; null = unread. */
  seen_at: string | null;
}

/** The dismissed ids of the old per-browser list; empty when storage is unavailable or holds garbage. */
export function readDismissed(storage: Pick<Storage, 'getItem'> | null = safeStorage()): string[] {
  try {
    const raw = storage?.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function clearDismissed(): void {
  try {
    safeStorage()?.removeItem(DISMISSED_KEY);
  } catch {
    // A blocked storage: nothing to remove.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The rows to show: a hidden id hides a dismissible notice only. */
export function visibleAnnouncements<R extends Announcement>(rows: readonly R[], hidden: readonly string[]): R[] {
  return rows.filter((r) => !r.dismissible || !hidden.includes(r.id));
}

function isSpanish(language: string | undefined): boolean {
  return (language ?? '').toLowerCase().startsWith('es');
}

/** The title and body in the reader's language; Spanish when the locale is `es`, English otherwise. */
export function localizedText(row: Announcement, language: string | undefined): { title: string; body: string } {
  return isSpanish(language) ? { title: row.title_es, body: row.body_es } : { title: row.title_en, body: row.body_en };
}

/** A portal-relative path (the 0285 CHECK): one leading slash, never `//host`, no scheme. */
const PORTAL_PATH = /^\/(?!\/)[A-Za-z0-9/_?=&.-]{0,199}$/;

/** The call to action in the reader's language, or null when the row has none (or an off-portal path). */
export function localizedLink(row: Announcement, language: string | undefined): { path: string; label: string } | null {
  const path = row.link_path;
  const label = isSpanish(language) ? row.link_label_es : row.link_label_en;
  if (!path || !PORTAL_PATH.test(path) || !label || label.trim() === '') return null;
  return { path, label };
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function announceChange(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ANNOUNCEMENTS_CHANGED));
}

/** Runs `read` when the tab comes back, on focus, every `REFRESH_MS`, and on `ANNOUNCEMENTS_CHANGED`. */
function useRefresh(enabled: boolean, read: () => void) {
  const readRef = React.useRef(read);
  readRef.current = read;
  React.useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (isVisible()) readRef.current();
    };
    const onFocus = () => readRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener(ANNOUNCEMENTS_CHANGED, onFocus);
    const id = setInterval(() => {
      if (isVisible()) readRef.current();
    }, REFRESH_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(ANNOUNCEMENTS_CHANGED, onFocus);
      clearInterval(id);
    };
  }, [enabled]);
}

export interface AnnouncementsState {
  /** The notices to show now (dismissed ones removed), in the server's order. */
  items: Announcement[];
  dismiss(id: string): void;
  reload(): void;
}

/** `enabled` false reads nothing (the layout passes it while the agency context is not ready). */
export function useAnnouncements(enabled = true): AnnouncementsState {
  const { client } = useAuth();
  const [rows, setRows] = React.useState<Announcement[]>([]);
  // Hidden on this page: optimistic dismissals and the legacy ids still being migrated.
  const [hidden, setHidden] = React.useState<string[]>(() => readDismissed());
  const seq = React.useRef(0);
  const migrating = React.useRef(false);

  const migrate = React.useCallback(
    async (served: Announcement[]) => {
      const legacy = readDismissed();
      if (legacy.length === 0 || migrating.current) return;
      migrating.current = true;
      try {
        const ids = served.filter((r) => r.dismissible && legacy.includes(r.id)).map((r) => r.id);
        let failed = false;
        for (const id of ids) {
          const { error } = await client.rpc('announcement_dismiss', { p_id: id });
          if (error) {
            failed = true;
            console.warn('useAnnouncements: migrating a dismissal failed', error);
          }
        }
        if (!failed) clearDismissed();
        if (ids.length > 0 && !failed) announceChange();
      } finally {
        migrating.current = false;
      }
    },
    [client],
  );

  const read = React.useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    const { data, error } = await client.rpc('my_announcements');
    if (mine !== seq.current) return;
    if (error) {
      console.warn('useAnnouncements: my_announcements failed', error);
      return;
    }
    const served = ((data ?? []) as Announcement[]).filter((r) => r && typeof r.id === 'string');
    setRows(served);
    void migrate(served);
  }, [client, enabled, migrate]);

  React.useEffect(() => {
    if (!enabled) {
      setRows([]);
      return;
    }
    void read();
  }, [read, enabled]);

  useRefresh(enabled, () => void read());

  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  const dismiss = React.useCallback(
    (id: string) => {
      const target = rowsRef.current.find((r) => r.id === id);
      if (!target || !target.dismissible) return;
      setHidden((prev) => (prev.includes(id) ? prev : [...prev, id]));
      void (async () => {
        const { error } = await client.rpc('announcement_dismiss', { p_id: id });
        if (error) {
          console.warn('useAnnouncements: announcement_dismiss failed', error);
          setHidden((prev) => prev.filter((x) => x !== id));
          toast.error(i18n.t('announcements.dismissFailed'));
          return;
        }
        announceChange();
      })();
    },
    [client],
  );

  const reload = React.useCallback(() => void read(), [read]);

  return { items: React.useMemo(() => visibleAnnouncements(rows, hidden), [rows, hidden]), dismiss, reload };
}

export interface UnreadState {
  count: number;
  reload(): void;
}

/** The topbar's unread count (`my_announcements_unread_count`); 0 while disabled, on failure or garbage. */
export function useAnnouncementsUnread(enabled = true): UnreadState {
  const { client } = useAuth();
  const [count, setCount] = React.useState(0);
  const seq = React.useRef(0);

  const read = React.useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    const { data, error } = await client.rpc('my_announcements_unread_count');
    if (mine !== seq.current) return;
    if (error) {
      console.warn('useAnnouncementsUnread: my_announcements_unread_count failed', error);
      setCount(0);
      return;
    }
    const n = Number(data);
    setCount(Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
  }, [client, enabled]);

  React.useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    void read();
  }, [read, enabled]);

  useRefresh(enabled, () => void read());

  return { count, reload: React.useCallback(() => void read(), [read]) };
}

export interface InboxState {
  status: 'loading' | 'ready' | 'error';
  items: InboxAnnouncement[];
  hasMore: boolean;
  loadingMore: boolean;
  loadMore(): void;
  /** True when the server stamped it read. */
  markRead(id: string): Promise<boolean>;
  /** Marks every loaded unread row; true when every one succeeded. */
  markAllRead(): Promise<boolean>;
  reload(): void;
}

/** Everything the caller received (`my_announcement_inbox`), newest first, `INBOX_PAGE` rows at a time. */
export function useAnnouncementInbox(): InboxState {
  const { client } = useAuth();
  const [items, setItems] = React.useState<InboxAnnouncement[]>([]);
  const [status, setStatus] = React.useState<InboxState['status']>('loading');
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const seq = React.useRef(0);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const fetchPage = React.useCallback(
    async (offset: number) => {
      const { data, error } = await client.rpc('my_announcement_inbox', { p_limit: INBOX_PAGE, p_offset: offset });
      if (error) {
        console.warn('useAnnouncementInbox: my_announcement_inbox failed', error);
        return null;
      }
      return ((data ?? []) as InboxAnnouncement[]).filter((r) => r && typeof r.id === 'string');
    },
    [client],
  );

  const reload = React.useCallback(async () => {
    const mine = ++seq.current;
    const page = await fetchPage(0);
    if (mine !== seq.current) return;
    if (!page) {
      setStatus((s) => (s === 'ready' ? s : 'error'));
      return;
    }
    setItems(page);
    setHasMore(page.length === INBOX_PAGE);
    setStatus('ready');
  }, [fetchPage]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const loadMore = React.useCallback(() => {
    void (async () => {
      setLoadingMore(true);
      const mine = seq.current;
      const page = await fetchPage(itemsRef.current.length);
      setLoadingMore(false);
      if (!page || mine !== seq.current) return;
      setItems((prev) => {
        const known = new Set(prev.map((r) => r.id));
        return [...prev, ...page.filter((r) => !known.has(r.id))];
      });
      setHasMore(page.length === INBOX_PAGE);
    })();
  }, [fetchPage]);

  const stamp = React.useCallback(
    async (id: string): Promise<boolean> => {
      const { data, error } = await client.rpc('announcement_dismiss', { p_id: id });
      if (error) {
        console.warn('useAnnouncementInbox: announcement_dismiss failed', error);
        return false;
      }
      const at = (data as { dismissed_at?: string } | null)?.dismissed_at ?? new Date().toISOString();
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, seen_at: at } : r)));
      return true;
    },
    [client],
  );

  const markRead = React.useCallback(
    async (id: string) => {
      const ok = await stamp(id);
      if (ok) announceChange();
      return ok;
    },
    [stamp],
  );

  const markAllRead = React.useCallback(async () => {
    const unread = itemsRef.current.filter((r) => !r.seen_at).map((r) => r.id);
    let all = true;
    for (const id of unread) {
      if (!(await stamp(id))) all = false;
    }
    if (unread.length > 0) announceChange();
    return all;
  }, [stamp]);

  return {
    status,
    items,
    hasMore,
    loadingMore,
    loadMore,
    markRead,
    markAllRead,
    reload: React.useCallback(() => void reload(), [reload]),
  };
}
